/**
 * @file JobRowSkeleton.tsx
 *
 * @description Provides a loading skeleton placeholder for pending job rows in
 * the home page view.
 *
 * @module Components/Skeletons
 * @requires Material-UI
 * @requires React
 */

import React from "react";
import { Box, Card, CardContent, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid";
import { alpha, useTheme } from "@mui/material/styles";

interface JobRowSkeletonProps {
  reportName?: string;
  /** Opens the job's generation surface; the row becomes a button. */
  onClick?: () => void;
}

/**
 * @function JobRowSkeleton
 *
 * @description This component creates a job placeholder UI element that mimics the structure of
 * a card in the home page view of reports.
 *
 * @returns {JSX.Element} A skeleton placeholder UI for a card in the home page view.
 */
export const JobRowSkeleton: React.FC<JobRowSkeletonProps> = ({
  reportName,
  onClick,
}) => {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      {...(onClick
        ? {
            onClick,
            role: "button",
            tabIndex: 0,
            "aria-label": `View generation progress: ${reportName ?? "report"}`,
            onKeyDown: (event: React.KeyboardEvent) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick();
              }
            },
          }
        : {})}
      sx={{
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
        ...(onClick ? { cursor: "pointer" } : {}),
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          borderRadius: "4px 0 0 4px",
          bgcolor: alpha(theme.palette.grey[400], 0.5),
        },
      }}
    >
      <CardContent
        sx={{
          p: 1.5,
          pl: 2.5,
          pr: 1,
          "&:last-child": { pb: 1.5 },
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Main Info Section */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Skeleton
              variant="rounded"
              width={100}
              height={26}
              sx={{ borderRadius: 2, mb: 1.5 }}
            />
            <Skeleton variant="text" width="85%" height={28} />
            <Skeleton variant="text" width="60%" height={20} />
          </Grid>

          {/* Reported By Section */}
          <Grid size={{ xs: 6, md: 3 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.grey[500], 0.04),
              }}
            >
              <Skeleton
                variant="text"
                width="50%"
                height={14}
                sx={{ mb: 0.5 }}
              />
              <Skeleton variant="text" width="80%" height={20} />
              <Skeleton variant="text" width="60%" height={16} />
              <Skeleton variant="text" width="70%" height={14} />
            </Box>
          </Grid>

          {/* Modified By Section */}
          <Grid size={{ xs: 6, md: 3 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.grey[500], 0.04),
              }}
            >
              <Skeleton
                variant="text"
                width="50%"
                height={14}
                sx={{ mb: 0.5 }}
              />
              <Skeleton variant="text" width="80%" height={20} />
              <Skeleton variant="text" width="70%" height={14} />
            </Box>
          </Grid>

          {/* Actions Section */}
          <Grid size={{ xs: 12, md: 1 }}>
            <Box
              display={{ xs: "none", md: "flex" }}
              flexDirection="column"
              gap={1}
              alignItems="center"
            >
              <Skeleton variant="circular" width={36} height={36} />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
